<script setup lang="ts">
  import { PhoneNumbersService, type SuperUserPhoneNumber as PhoneNumber } from '@/types/generated'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse
  } from '@/components/admin/PaginatedTable.vue'
  import CustomerTableDataCell from '@/components/admin/table/CustomerTableDataCell.vue'
  import { ROUTES } from '@/router/routes'
  import UniqueTableDataCell from '@/components/admin/table/UniqueTableDataCell.vue'
  import { formatDateTimeGerman } from '../../../lib/util/formatting'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { TableRow, TableCell } from '@/components/ui/table'
  import type { Filter } from '@/components/admin/PaginatedTable.vue'
  import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-vue-next'
  import { ref, nextTick } from 'vue'

  const LANGUAGE_STYLES: Record<string, { label: string; classes: string }> = {
    de: { label: 'DE', classes: 'bg-purple-50 text-purple-700 border border-purple-200' },
    en: { label: 'EN', classes: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
    fr: { label: 'FR', classes: 'bg-violet-50 text-violet-700 border border-violet-200' },
    pl: { label: 'PL', classes: 'bg-blue-50 text-blue-700 border border-blue-200' }
  }

  const tableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)

  const filterableFields: Filter[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'multi-select',
      value: 'active',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' }
      ]
    },
    {
      key: 'language',
      label: 'Language',
      type: 'multi-select',
      options: [
        { label: 'DE', value: 'de' },
        { label: 'FR', value: 'fr' },
        { label: 'EN', value: 'en' },
        { label: 'PL', value: 'pl' }
      ]
    }
  ]

  // Sorting state
  const sortField = ref('created_at')
  const sortOrder = ref<'asc' | 'desc'>('desc')

  const handleSort = (field: string) => {
    if (sortField.value === field) {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
    } else {
      sortField.value = field
      sortOrder.value = 'asc'
    }
    nextTick(() => tableRef.value?.resetAndRefresh())
  }

  const getSortIcon = (field: string) => {
    if (sortField.value !== field) return ArrowUpDown
    return sortOrder.value === 'asc' ? ArrowUp : ArrowDown
  }

  const isSorted = (field: string) => sortField.value === field

  // Helper to display agent name with truncated ID fallback
  const getAgentDisplayName = (agent: PhoneNumber['agent']): string => {
    if (agent.agent_name) return agent.agent_name
    if (agent.retell_agent_id) return agent.retell_agent_id.substring(0, 8) + '...'
    return '-'
  }

  const fetchPhoneNumbers = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<unknown>> => {
    const filters = options?.filters || []
    const getFilter = (key: string) => filters.find((f) => f.key === key)?.value
    const orderingParam = sortOrder.value === 'desc' ? `-${sortField.value}` : sortField.value

    // Parse status multi-select
    const statusVal = getFilter('status')
    const statusSet = statusVal ? new Set(statusVal.split(',')) : new Set<string>()
    const hasActive = statusSet.has('active')
    const hasInactive = statusSet.has('inactive')
    // Only filter if exactly one is selected
    const activeParam =
      hasActive && !hasInactive ? true : !hasActive && hasInactive ? false : undefined

    // Parse language multi-select
    const languageVal = getFilter('language')

    const response = await PhoneNumbersService.phoneNumbersList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search,
      active: activeParam,
      language: languageVal,
      ordering: orderingParam
    })
    return {
      results: response.results as unknown[],
      next: response.next ?? null,
      previous: response.previous ?? null,
      count: response.count
    }
  }
</script>
<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Phone numbers', to: ROUTES.ADMIN.VOICE.PHONE_NUMBER_LIST }
          ]"
        />
        <div class="flex items-center justify-end gap-x-2">
          <router-link
            class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
              text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
              focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
            :to="ROUTES.ADMIN.VOICE.PHONE_NUMBER_CREATE"
          >
            <svg
              class="hidden size-3 flex-shrink-0 sm:block"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M8 1C8.55228 1 9 1.44772 9 2V7L14 7C14.5523 7 15 7.44771 15 8C15 8.55228 14.5523 9 14 9L9 9V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V9.00001L2 9.00001C1.44772 9.00001 1 8.5523 1 8.00001C0.999999 7.44773 1.44771 7.00001 2 7.00001L7 7.00001V2C7 1.44772 7.44772 1 8 1Z"
              ></path>
            </svg>
            Register phone number
          </router-link>
        </div>
      </div>
      <!-- End Page Header -->

      <!-- Users Table Card -->
      <div class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div>
          <!-- Tab Content -->
          <div class="">
            <!-- Table Section -->
            <div
              class="[&amp;::-webkit-scrollbar]:h-2 [&amp;::-webkit-scrollbar-thumb]:rounded-full
                [&amp;::-webkit-scrollbar-track]:bg-gray-100 [&amp;::-webkit-scrollbar-thumb]:bg-gray-300
                &amp;::-webkit-scrollbar-track]:bg-neutral-700 &amp;::-webkit-scrollbar-thumb]:bg-neutral-500
                overflow-x-auto"
            >
              <div class="inline-block min-w-full align-middle">
                <!-- Table -->
                <PaginatedTableView
                  ref="tableRef"
                  :fetchData="fetchPhoneNumbers"
                  searchableFields="UUID, phone number or customer name"
                  :filterableFields="filterableFields"
                >
                  <template #table-head>
                    <TableCell>UUID</TableCell>
                    <TableCell class="w-[1%] whitespace-nowrap px-5">
                      <button
                        class="inline-flex items-center gap-1 font-medium text-gray-700 transition-colors hover:text-gray-900"
                        :class="{ 'text-gray-900': isSorted('created_at') }"
                        @click="handleSort('created_at')"
                      >
                        Created
                        <component
                          :is="getSortIcon('created_at')"
                          class="h-4 w-4"
                        />
                      </button>
                    </TableCell>
                    <TableCell class="w-[1%] whitespace-nowrap px-5">
                      <button
                        class="inline-flex items-center gap-1 font-medium text-gray-700 transition-colors hover:text-gray-900"
                        :class="{ 'text-gray-900': isSorted('active') }"
                        @click="handleSort('active')"
                      >
                        Active
                        <component
                          :is="getSortIcon('active')"
                          class="h-4 w-4"
                        />
                      </button>
                    </TableCell>
                    <TableCell class="w-[1%] whitespace-nowrap px-5">
                      <button
                        class="inline-flex items-center gap-1 font-medium text-gray-700 transition-colors hover:text-gray-900"
                        :class="{ 'text-gray-900': isSorted('phone_number') }"
                        @click="handleSort('phone_number')"
                      >
                        PhoneNumber
                        <component
                          :is="getSortIcon('phone_number')"
                          class="h-4 w-4"
                        />
                      </button>
                    </TableCell>
                    <TableCell class="w-[1%] whitespace-nowrap px-5">Agent</TableCell>
                    <TableCell class="w-[1%] whitespace-nowrap px-5">Customer</TableCell>
                    <TableCell class="w-[1%] whitespace-nowrap px-5">Actions</TableCell>
                  </template>
                  <template #table-row="{ items: phoneNumbers }">
                    <TableRow
                      v-for="phoneNumber in phoneNumbers"
                      :key="phoneNumber.uuid"
                    >
                      <UniqueTableDataCell :truncate="true">
                        <router-link
                          :to="
                            ROUTES.ADMIN.VOICE.PHONE_NUMBER_DETAIL.replace(
                              ':uuid',
                              phoneNumber.uuid
                            )
                          "
                        >
                          {{ phoneNumber.uuid }}
                        </router-link>
                      </UniqueTableDataCell>
                      <TableCell class="whitespace-nowrap text-center">
                        {{ formatDateTimeGerman(phoneNumber.created_at) }}
                      </TableCell>
                      <TableCell class="text-center">
                        {{ phoneNumber.active ? 'Yes' : 'No' }}
                      </TableCell>
                      <TableCell class="whitespace-nowrap text-center">
                        {{ phoneNumber.phone_number }}
                      </TableCell>
                      <TableCell class="whitespace-nowrap text-center">
                        <div
                          v-if="phoneNumber.agent"
                          class="inline-flex items-center gap-2"
                        >
                          <span :title="phoneNumber.agent.retell_agent_id">
                            {{ getAgentDisplayName(phoneNumber.agent) }}
                          </span>
                          <span
                            v-if="
                              phoneNumber.agent.language &&
                              LANGUAGE_STYLES[phoneNumber.agent.language]
                            "
                            class="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none"
                            :class="LANGUAGE_STYLES[phoneNumber.agent.language]?.classes"
                          >
                            {{ LANGUAGE_STYLES[phoneNumber.agent.language]?.label }}
                          </span>
                        </div>
                        <span v-else>-</span>
                      </TableCell>
                      <CustomerTableDataCell :org="phoneNumber.org" />

                      <TableCell class="text-center">
                        <router-link
                          class="text-sm font-semibold text-blue-600 hover:text-blue-800"
                          :to="
                            ROUTES.ADMIN.VOICE.PHONE_NUMBER_DETAIL.replace(
                              ':uuid',
                              phoneNumber.uuid
                            )
                          "
                          type="button"
                        >
                          Details
                        </router-link>
                      </TableCell>
                    </TableRow>
                  </template>
                </PaginatedTableView>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
